import React from "react"
import {
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckGroup,
  CheckGroupOperator,
  CheckLeaf,
  OPERATOR_AND,
  OPERATOR_OR
} from "lib/types/workflow"

import LeafCheckEditor from "./LeafCheckEditor"
import { makeDefaultLeaf } from "./checkFieldHelpers"
import {
  ChildActions,
  ChildHeader,
  GroupActionsRow,
  GroupChild,
  GroupContainer,
  GroupHeader,
  OperatorButton,
  OperatorToggle,
  SmallActionButton
} from "./NodeEditorSidebar.styled"

type Props = {
  value: CheckGroup
  onChange: (next: CheckGroup) => void
  /** Called when the user collapses this group via "Unwrap": replaces the
   *  whole group with a single child expression. Only invoked when the group
   *  has exactly one child. Optional — top-level groups handle this themselves. */
  onUnwrap?: (child: CheckExpression) => void
  /** Recursion depth, used to cap nesting at a reasonable level. */
  depth?: number
}

const MAX_NESTING_DEPTH = 4

const CheckGroupEditor = ({ value, onChange, onUnwrap, depth = 0 }: Props) => {
  const updateChild = (idx: number, next: CheckExpression) => {
    const children = [...value.children]
    children[idx] = next
    onChange({ ...value, children })
  }

  const removeChild = (idx: number) => {
    const children = value.children.filter((_, i) => i !== idx)
    onChange({ ...value, children })
  }

  const wrapChildInGroup = (idx: number) => {
    const child = value.children[idx]
    if (!child) return
    const wrapped: CheckGroup = {
      kind: CHECK_KIND_GROUP,
      operator: OPERATOR_AND,
      children: [child, makeDefaultLeaf()]
    }
    updateChild(idx, wrapped)
  }

  const addLeaf = () => {
    onChange({ ...value, children: [...value.children, makeDefaultLeaf()] })
  }

  // Default the nested group to the *opposite* operator. Nesting the same
  // operator (AND inside AND, OR inside OR) flattens back to a single group
  // on reload, so the only structurally meaningful nested group is the
  // alternative. The button label spells this out so it's not a surprise.
  const nestedOperator: CheckGroupOperator =
    value.operator === OPERATOR_AND ? OPERATOR_OR : OPERATOR_AND

  const addNestedGroup = () => {
    const nested: CheckGroup = {
      kind: CHECK_KIND_GROUP,
      operator: nestedOperator,
      children: [makeDefaultLeaf(), makeDefaultLeaf()]
    }
    onChange({ ...value, children: [...value.children, nested] })
  }

  const setOperator = (operator: CheckGroupOperator) => {
    onChange({ ...value, operator })
  }

  const canNestDeeper = depth < MAX_NESTING_DEPTH

  return (
    <GroupContainer>
      <GroupHeader>
        <OperatorToggle role="group" aria-label="Match condition">
          <OperatorButton
            type="button"
            $active={value.operator === OPERATOR_AND}
            onClick={() => setOperator(OPERATOR_AND)}
          >
            All (AND)
          </OperatorButton>
          <OperatorButton
            type="button"
            $active={value.operator === OPERATOR_OR}
            onClick={() => setOperator(OPERATOR_OR)}
          >
            Any (OR)
          </OperatorButton>
        </OperatorToggle>
        {onUnwrap && value.children.length === 1 && (
          <SmallActionButton
            type="button"
            onClick={() => onUnwrap(value.children[0])}
          >
            Unwrap group
          </SmallActionButton>
        )}
      </GroupHeader>

      {value.children.map((child, idx) => (
        <GroupChild key={idx}>
          <ChildHeader>
            <span>
              {child.kind === CHECK_KIND_GROUP
                ? `Nested ${child.operator} group`
                : `Check ${idx + 1}`}
            </span>
            <ChildActions>
              {child.kind === CHECK_KIND_LEAF && canNestDeeper && (
                <SmallActionButton
                  type="button"
                  onClick={() => wrapChildInGroup(idx)}
                  title="Wrap this check in a nested group"
                >
                  Group with…
                </SmallActionButton>
              )}
              <SmallActionButton
                type="button"
                $destructive
                onClick={() => removeChild(idx)}
                disabled={value.children.length <= 1}
                title="Remove this check"
              >
                Remove
              </SmallActionButton>
            </ChildActions>
          </ChildHeader>

          {child.kind === CHECK_KIND_LEAF ? (
            <LeafCheckEditor
              value={child}
              onChange={(next: CheckLeaf) => updateChild(idx, next)}
            />
          ) : (
            <CheckGroupEditor
              value={child}
              onChange={(next: CheckGroup) => updateChild(idx, next)}
              onUnwrap={(unwrapped) => updateChild(idx, unwrapped)}
              depth={depth + 1}
            />
          )}
        </GroupChild>
      ))}

      <GroupActionsRow>
        <SmallActionButton type="button" onClick={addLeaf}>
          + Add check
        </SmallActionButton>
        {canNestDeeper && (
          <SmallActionButton type="button" onClick={addNestedGroup}>
            + Add nested {nestedOperator} group
          </SmallActionButton>
        )}
      </GroupActionsRow>
    </GroupContainer>
  )
}

export default CheckGroupEditor
